import { Button, Classes, Intent } from "@blueprintjs/core"
import React from "react"
import { NavControlResult } from "../../../../../api/reconciliation"
import { SubmitConfirmationNote, SubmitDialog } from "../Reconciliation.styled"
import { isBreach, plural, PriceGapBreaches } from "../reconciliation.utils"
import ControlBreaches from "./ControlBreaches"

interface SubmitConfirmationProps {
  isOpen: boolean
  fund: string
  navDate: string
  // The NAV controls as the operator sees them (operatorNavControls, owned
  // by the page hook); only those that did not pass are listed.
  navControls: NavControlResult[]
  // The rows whose price-gap control did not pass as the operator sees
  // them, with the thresholds and the NAV reference the control ran with
  // (the hook's priceGapBreaches); absent when the server ran no controls.
  priceGapBreaches?: PriceGapBreaches
  // Called with the acknowledgement (CONTEXT.md): true only when the dialog
  // listed breaches and the operator clicked "Acknowledge and submit".
  onConfirm: (acknowledged: boolean) => void
  onCancel: () => void
}

/**
 * The confirmation that precedes a submission (CONTEXT.md, "Acknowledgement").
 * On a clean day it asks the fund and date question, as the browser confirm
 * did. When the NAV control reported breaches or unverified classes, or the
 * price-gap control breached or unverified rows, it lists each one — class
 * or security, both figures, gap, threshold — in one list and asks for a
 * single acknowledgement. No comment field: a routine acknowledged gap
 * costs one click, and the acknowledgement clears nothing.
 */
const SubmitConfirmation: React.FC<SubmitConfirmationProps> = ({
  isOpen,
  fund,
  navDate,
  navControls,
  priceGapBreaches,
  onConfirm,
  onCancel,
}) => {
  // The same reading ControlBreaches lists: the NAV controls that did not
  // pass, the price-gap breaches' rows.
  const classCount = navControls.filter(isBreach).length
  const securityCount = priceGapBreaches?.rows.length ?? 0
  const hasBreaches = classCount + securityCount > 0
  const targets = [
    ...(classCount > 0 ? [plural(classCount, "class", "classes")] : []),
    ...(securityCount > 0 ? [plural(securityCount, "security", "securities")] : []),
  ].join(" and ")
  return (
    <SubmitDialog
      title="Submit NAV"
      className="bp5-dark"
      isOpen={isOpen}
      isCloseButtonShown={false}
      onClose={onCancel}
    >
      <div className={Classes.DIALOG_BODY}>
        <p>
          Submit NAV for {fund} — {navDate}?
        </p>
        {hasBreaches && (
          <>
            <SubmitConfirmationNote>
              The controls did not pass for {targets}. Submitting records your acknowledgement
              that you have seen them.
            </SubmitConfirmationNote>
            <ControlBreaches navControls={navControls} priceGapBreaches={priceGapBreaches} />
          </>
        )}
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={onCancel} text="Cancel" />
          <Button
            onClick={() => onConfirm(hasBreaches)}
            intent={hasBreaches ? Intent.WARNING : Intent.SUCCESS}
            text={hasBreaches ? "Acknowledge and submit" : "Submit"}
          />
        </div>
      </div>
    </SubmitDialog>
  )
}

export default SubmitConfirmation
